import { forceReauth } from "./auth";
import { isRequiredPasswordChangeLogoutInProgress } from "./passwordChangeLogout";

const PUBLIC_ROUTE_PREFIXES = [
    "/login",
    "/login/forgot-password",
    "/signup",
    "/checkout",
    "/auth/clever/callback",
    "/login/callback",
    "/auth/set-password",
    "/onboarding/welcome",
    "/subscription/success",
    "/subscription/cancel",
    "/terms",
    "/privacy",
    "/data",
    "/guardian/no-athletes",
    "/guardian/select",
    "/marketinghub",
];

/** Endpoints that may legitimately return 401 during login / MFA / invitation flows. */
const AUTH_401_EXEMPT_PATHS = [
    "/api/auth/login",
    "/api/auth/mfa/verify",
    "/api/auth/mfa/resend",
    "/api/auth/mfa/cancel",
    "/api/auth/validate-invitation",
    "/api/auth/redeem-invitation",
    "/api/auth/forgot-password",
    "/api/auth/logout",
];

let installed = false;
let redirecting = false;

function resolveRequestUrl(input: RequestInfo | URL): string {
    if (typeof input === "string") {
        return input;
    }
    if (input instanceof URL) {
        return input.href;
    }
    return input.url;
}

function pathnameFromRequestUrl(requestUrl: string): string {
    try {
        return new URL(requestUrl, window.location.origin).pathname;
    } catch {
        return requestUrl;
    }
}

export function isPublicAppRoute(pathname: string = window.location.pathname): boolean {
    if (pathname === "/") {
        return true;
    }
    return PUBLIC_ROUTE_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
}

export function isOurApiRequest(requestUrl: string): boolean {
    return pathnameFromRequestUrl(requestUrl).includes("/api/");
}

export function isAuth401ExemptRequest(requestUrl: string): boolean {
    const path = pathnameFromRequestUrl(requestUrl);
    return AUTH_401_EXEMPT_PATHS.some(
        (exempt) => path === exempt || path.endsWith(exempt),
    );
}

export function isRefreshRequest(requestUrl: string): boolean {
    const path = pathnameFromRequestUrl(requestUrl);
    return path === "/api/auth/refresh" || path.endsWith("/api/auth/refresh");
}

/**
 * Returns true when a 401 from this request should force a clean re-login.
 * Protected API 401s are left to apiClient so it can refresh first.
 */
export function shouldRedirectOn401(
    requestUrl: string,
    pathname: string = window.location.pathname,
): boolean {
    if (isRequiredPasswordChangeLogoutInProgress()) {
        return false;
    }
    if (isPublicAppRoute(pathname)) {
        return false;
    }
    if (!isOurApiRequest(requestUrl)) {
        return false;
    }
    if (isAuth401ExemptRequest(requestUrl)) {
        return false;
    }
    return isRefreshRequest(requestUrl);
}

/**
 * Forces re-login after an unexpected 401. Safe to call from fetch wrappers.
 * @returns true when a redirect was triggered.
 */
export function handleUnauthorizedApiResponse(
    requestUrl: string,
    pathname: string = window.location.pathname,
): boolean {
    if (!shouldRedirectOn401(requestUrl, pathname)) {
        return false;
    }
    if (redirecting) {
        return true;
    }
    redirecting = true;
    forceReauth("session_expired");
    return true;
}

/** Patches window.fetch so a failed refresh 401 redirects to login. */
export function installAuthFetchInterceptor(): void {
    if (installed || typeof window === "undefined") {
        return;
    }
    installed = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
        const response = await originalFetch(...args);
        if (response.status === 401) {
            handleUnauthorizedApiResponse(resolveRequestUrl(args[0]));
        }
        return response;
    };
}

/** Test helper */
export function resetAuthFetchInterceptorForTests(): void {
    installed = false;
    redirecting = false;
}
